import React, { useEffect, useState, useRef, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { GMap } from "primereact/gmap";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { DispositionsList } from "../../forms/DispositionsList/DispositionsList";
import { DispositionsByRequestOverviewNew } from "../../forms/DispositionsByRequestOverviewNew/DispositionsByRequestOverviewNew";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { Request, RequestType } from "../../models/Request";
import { Disposition } from "../../models/Disposition";
import { Utility } from "../../core/Utility";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { loadGoogleMaps, removeGoogleMaps } from "../DashboardPage/googlemaps";
import {
  getAllRequestsByDate,
  getAllDispositionsByDate,
  getOneRequest,
} from "../../services/NewVersionService";
import { getAllPartners } from "../../services/PartnerService";
import "./DispositionsByRequestPageNew.scss";

export const DispositionsByRequestPageNew = () => {
  const { t } = useTranslation();

  const [partnersApi, setPartnersApi] = useState<any[]>([]);
  const [allRequests, setAllRequests] = useState<Request[]>([]);
  const [allDispositions, setAllDispositions] = useState<Disposition[]>([]);
  const [googleMapsReady, setGoogleMapsReady] = useState(false);
  const [ready, setReady] = useState<boolean>(false);
  const [listVisible, setListVisible] = useState<boolean>(false);
  const [selectedDispositions, setSelectedDispositions] = useState<
    Disposition[]
  >([]);
  const [dispositionsOverlay, setDispositionsOverlay] = useState<any[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<Request>();
  const [requestOverviewVisible, setRequestOverviewVisible] =
    useState<boolean>(false);
  const [dateFrom, setDateFrom] = useState<Date>(
    new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate() - 5,
      0,
      0,
      1,
    ),
  ); //5 days ago 00:00:01h

  const [processing, setProcessing] = useState(false);
  const [filters, setFilters] = useState({
    partnerName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    requestNumberFormatted: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    place: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type: { value: null, matchMode: FilterMatchMode.EQUALS },
  });

  useEffect(() => {
    loadGoogleMaps(() => {
      setGoogleMapsReady(true);
    });

    setProcessing(true);
    Promise.all([
      getAllRequestsByDate(
        new Date(
          new Date().getFullYear(),
          new Date().getMonth(),
          new Date().getDate() - 5,
          0,
          0,
          1,
        ),
      ),
      getAllDispositionsByDate(
        new Date(
          new Date().getFullYear(),
          new Date().getMonth(),
          new Date().getDate() - 5,
          0,
          0,
          1,
        ),
      ),
      getAllPartners(),
    ])
      .then((response) => {
        const allNewRequests = response[0].data.result;
        const sortedRequestsByDate = allNewRequests.sort(
          (a: Request, b: Request) =>
            new Date(a?.date as Date).getTime() <
            new Date(b?.date as Date).getTime()
              ? 1
              : -1,
        );
        setAllRequests(sortedRequestsByDate);
        const allNewDispositions = response[1].data.result;
        setAllDispositions(allNewDispositions);
        const partnersRaw = response[2]?.data?.result ?? response[2]?.data;
        setPartnersApi(Array.isArray(partnersRaw) ? partnersRaw : []);
      })
      .finally(() => {
        setProcessing(false);
        setReady(true);
      });

    return () => {
      removeGoogleMaps();
    };
  }, []);

  useEffect(() => {
    setProcessing(true);
    Promise.all([
      getAllRequestsByDate(new Date(dateFrom)),
      getAllDispositionsByDate(new Date(dateFrom)),
    ])
      .then((response) => {
        const allNewRequests = response[0].data.result;
        const sortedRequestsByDate = allNewRequests.sort(
          (a: Request, b: Request) =>
            new Date(a?.date as Date).getTime() <
            new Date(b?.date as Date).getTime()
              ? 1
              : -1,
        );
        setAllRequests(sortedRequestsByDate);
        const allNewDispositions = response[1].data.result;
        setAllDispositions(allNewDispositions);
      })
      .finally(() => {
        setProcessing(false);
        setReady(true);
      });
  }, [dateFrom]);

  const partnerById = useMemo(() => {
    const map = new Map<string, any>();
    for (const p of partnersApi ?? []) {
      if (p?.id) map.set(p.id.toString(), p);
    }
    return map;
  }, [partnersApi]);

  useEffect(() => {
    if (googleMapsReady && ready) {
      initOverlays();
    }
  }, [googleMapsReady, ready]);

  useEffect(() => {
    for (let overlay of dispositionsOverlay) {
      animateCircle(overlay);
    }
  }, [dispositionsOverlay]);

  const toast = useRef(null);

  const initOverlays = () => {
    const lineSymbol = {
      path: "M0 3.5A1.5 1.5 0 0 1 1.5 2h9A1.5 1.5 0 0 1 12 3.5V5h1.02a1.5 1.5 0 0 1 1.17.563l1.481 1.85a1.5 1.5 0 0 1 .329.938V10.5a1.5 1.5 0 0 1-1.5 1.5H14a2 2 0 1 1-4 0H5a2 2 0 1 1-3.998-.085A1.5 1.5 0 0 1 0 10.5v-7zm1.294 7.456A1.999 1.999 0 0 1 4.732 11h5.536a2.01 2.01 0 0 1 .732-.732V3.5a.5.5 0 0 0-.5-.5h-9a.5.5 0 0 0-.5.5v7a.5.5 0 0 0 .294.456zM12 10a2 2 0 0 1 1.732 1h.768a.5.5 0 0 0 .5-.5V8.35a.5.5 0 0 0-.11-.312l-1.48-1.85A.5.5 0 0 0 13.02 6H12v4zm-9 1a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm9 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2z",
      scale: 1,
      strokeColor: "#393",
      strokeOpacity: 1,
      rotation: 0,
    };

    const lines: any[] = [];

    for (let disposition of allDispositions) {
      if (disposition.inputWarehouse && disposition.outputWarehouse) {
        const newLineSymbol = _.cloneDeep(lineSymbol);
        const newLine = new google.maps.Polyline({
          path: [
            {
              lat: parseFloat(disposition.outputWarehouse.latitude!),
              lng: parseFloat(disposition.outputWarehouse.longitude!),
            },
            {
              lat: parseFloat(disposition.inputWarehouse.latitude!),
              lng: parseFloat(disposition.inputWarehouse.longitude!),
            },
          ],
          icons: [
            {
              icon: newLineSymbol,
              offset: "100%",
              fixedRotation: true,
            },
          ],
          strokeWeight: 8,
          strokeColor: "#fff",
          strokeOpacity: 0.6,
          clickable: true,
        });
        lines.push(newLine);
        google.maps.event.addListener(newLine, "click", () => {
          const newSelectedDispositions = allDispositions.filter((d) => {
            if (d.inputWarehouse && d.outputWarehouse) {
              return (
                (disposition.inputWarehouse!.id == d.inputWarehouse!.id &&
                  disposition.outputWarehouse!.id == d.outputWarehouse!.id) ||
                (disposition.inputWarehouse!.id == d.outputWarehouse!.id &&
                  disposition.outputWarehouse!.id == d.inputWarehouse!.id)
              );
            }
            return false;
          });
          setSelectedDispositions(newSelectedDispositions);
          setListVisible(true);
        });
      }
    }
    setDispositionsOverlay(lines);
  };

  const animateCircle = (line: google.maps.Polyline) => {
    let count = 0;

    window.setInterval(() => {
      count = (count + 1) % 200;

      const icons = line.get("icons");

      if (icons) icons[0].offset = count / 2 + "%";
      line.set("icons", icons);
    }, 20);
  };

  const {
    control,
    formState: { errors },
  } = useForm({
    mode: "all",
  });

  const onDateFromChange = (property: string, value: Date | null) => {
    setDateFrom(value as Date);
  };

  const formatType = (rowData: Request) => {
    const dispositionIndex = allDispositions.findIndex(
      (disposition) => disposition.request?.id == rowData?.id,
    );
    const isDispositions = dispositionIndex > -1 ? true : false; //is exist any disposition for request
    if (rowData.type === RequestType.Import)
      return (
        <span
          style={isDispositions ? { color: "#00A553" } : { color: "#DC3545" }}
        >
          {t("Prijem")}
        </span>
      );
    if (rowData.type === RequestType.Export)
      return (
        <span
          style={isDispositions ? { color: "#00A553" } : { color: "#DC3545" }}
        >
          {t("Prodaja")}
        </span>
      );
    if (rowData.type === RequestType.Relocation)
      return (
        <span
          style={isDispositions ? { color: "#00A553" } : { color: "#DC3545" }}
        >
          {t("Premeštaj")}
        </span>
      );
  };

  const typeOptions = [
    { label: t("Prijem"), value: RequestType.Import },
    { label: t("Prodaja"), value: RequestType.Export },
    { label: t("Premeštaj"), value: RequestType.Relocation },
  ];

  const typeItemTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option.label}</span>
      </div>
    );
  };

  const typeRowFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={typeOptions}
        onChange={(e) => {
          options.filterApplyCallback(e.value);
        }}
        itemTemplate={typeItemTemplate}
        placeholder="Izaberite"
        className="p-column-filter"
        showClear
      />
    );
  };

  const formatDate = (rowData: Request) => {
    return Utility.formatDate(new Date(rowData?.date as Date));
  };

  const formatPartnerName = (rowData: Request) => {
    const id = rowData?.partner?.id;
    if (!id) return "";
    return partnerById.get(id.toString())?.name ?? "";
  };

  const formatView = (rowData: Request) => {
    return (
      <div className="action-button">
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-eye"
          onButtonClick={() => {
            const id = rowData?.id;
            if (id) {
              getOneRequest(id)
                .then((resp: any) => {
                  const item = resp?.data?.result ?? resp?.data;
                  if (item) setSelectedRequest(item);
                })
                .then(() => setRequestOverviewVisible(true));
            }
          }}
        />
      </div>
    );
  };

  return (
    <PageLayout>
      <div className="map-wrapper">
        <div className="google-maps-row">
          {googleMapsReady && ready && (
            <GMap
              options={{
                center: {
                  lat: 44.787197,
                  lng: 20.457273,
                },
                zoom: 7,
              }}
              overlays={dispositionsOverlay}
              className="maps"
            />
          )}
        </div>
        <Toast ref={toast} position={"top-right"} />
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-chat-left-text header-title-icon"
              style={{
                fontSize: 16,
                marginLeft: "1rem",
                marginRight: "1rem",
                transform: "translate(0, 5px)",
              }}
            ></i>
            <span className="header-left-title">{t("Pregled zahteva od")}</span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              onValueChange={onDateFromChange}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              scrollable={true}
              scrollHeight="80vh"
              sortMode="single"
              removableSort
              filters={filters}
              filterDisplay="row"
              emptyMessage="Lista zahteva je prazna"
              value={allRequests}
              showGridlines
              stripedRows
            >
              <Column
                body={formatType}
                filter
                filterField={"type"}
                showFilterMenu={false}
                filterElement={typeRowFilterTemplate}
                header={t("Tip zahteva")}
                style={{ minWidth: "100px" }}
              />
              <Column
                body={formatDate}
                field="date"
                sortable
                header={t("Datum")}
                style={{ minWidth: "100px" }}
              />
              <Column
                field="requestNumberFormatted"
                header={t("Broj zahteva")}
                sortable
                filter
                style={{ minWidth: "110px" }}
              />
              <Column
                body={formatPartnerName}
                field="partnerName"
                header={t("Kupac / Dobavljač")}
                style={{ minWidth: "100px" }}
              />
              <Column
                field="place"
                filter
                header={t("Mesto utovara / istovara")}
                style={{ minWidth: "100px" }}
              />
              <Column
                body={formatView}
                header={t("Opcije")}
                style={{ minWidth: "100px" }}
              />
            </DataTable>
          </div>
        )}
        <DispositionsList
          visible={listVisible}
          dispositions={selectedDispositions}
          buttonStyle={{
            border: "none",
            background: "#ffffff",
            color: "#6C757D",
            marginRight: "1rem",
            margin: "0 1rem 0 0",
          }}
          buttonNewCityStyle={{
            border: "none",
            background: "rgba(21, 115, 71, 1)",
          }}
          buttonSaveStyle={{
            border: "none",
            background: "rgba(0, 165, 83, 1)",
          }}
          buttonInactiveStyle={{
            border: "none",
            background: "rgba(108, 117, 125, 1)",
          }}
          handleClose={() => {
            setListVisible(false);
            initOverlays();
          }}
        />
        <DispositionsByRequestOverviewNew
          visible={requestOverviewVisible}
          request={selectedRequest as Request}
          dateFrom={new Date(dateFrom)}
          buttonInactiveStyle={{
            border: "none",
            background: "rgba(108, 117, 125, 1)",
          }}
          handleClose={() => setRequestOverviewVisible(false)}
        />
      </div>
    </PageLayout>
  );
};
